/**
 * Email Builder State Management Hook
 * Includes undo/redo history stack
 */

import { useState, useCallback, useRef } from 'react';
import {
  EmailDocument,
  EmailRow,
  ContentBlock,
  Selection,
  GlobalStyles,
  RowLayout,
  BlockType,
  DEFAULT_GLOBAL_STYLES,
  createRow,
  createBlock,
  generateId,
} from './types';

export interface EmailBuilderState {
  document: EmailDocument;
  selection: Selection;
  canUndo: boolean;
  canRedo: boolean;
}

export interface EmailBuilderActions {
  // Row actions
  addRow: (layout: RowLayout, index?: number) => void;
  deleteRow: (rowId: string) => void;
  moveRow: (fromIndex: number, toIndex: number) => void;
  updateRow: (rowId: string, updates: Partial<EmailRow>) => void;
  duplicateRow: (rowId: string) => void;

  // Block actions
  addBlock: (columnId: string, type: BlockType, index?: number) => void;
  deleteBlock: (blockId: string) => void;
  moveBlock: (blockId: string, targetColumnId: string, targetIndex: number) => void;
  updateBlock: (blockId: string, updates: Partial<ContentBlock>) => void;
  duplicateBlock: (blockId: string) => void;

  // Selection actions
  select: (selection: Selection) => void;
  clearSelection: () => void;

  // Global actions
  updateGlobalStyles: (updates: Partial<GlobalStyles>) => void;
  reset: () => void;
  loadDocument: (doc: EmailDocument) => void;
  
  // Undo/Redo actions
  undo: () => void;
  redo: () => void;
}

const initialDocument: EmailDocument = {
  rows: [],
  globalStyles: DEFAULT_GLOBAL_STYLES,
};

// Maximum history entries to prevent memory issues
const MAX_HISTORY_SIZE = 50;

// Debounce time for batching rapid changes (like typing)
const HISTORY_DEBOUNCE_MS = 300;

export function useEmailBuilder(): EmailBuilderState & EmailBuilderActions {
  const [document, setDocumentInternal] = useState<EmailDocument>(initialDocument);
  const [selection, setSelection] = useState<Selection>({ type: null });
  
  // History stack: array of past document states
  const historyRef = useRef<EmailDocument[]>([initialDocument]);
  const historyIndexRef = useRef<number>(0);
  const lastChangeTimeRef = useRef<number>(0);
  
  // Force re-render when history changes
  const [, forceUpdate] = useState({});
  
  // Check if we can undo/redo
  const canUndo = historyIndexRef.current > 0;
  const canRedo = historyIndexRef.current < historyRef.current.length - 1;

  // Push a new state to history (with debouncing for rapid changes)
  const pushToHistory = useCallback((newDocument: EmailDocument, immediate = false) => {
    const now = Date.now();
    const timeSinceLastChange = now - lastChangeTimeRef.current;
    
    // If changes are happening rapidly, update the current history entry instead of creating new one
    if (!immediate && timeSinceLastChange < HISTORY_DEBOUNCE_MS && historyRef.current.length > 1) {
      // Update the current entry
      historyRef.current[historyIndexRef.current] = newDocument;
    } else {
      // Truncate any redo history (we're branching)
      historyRef.current = historyRef.current.slice(0, historyIndexRef.current + 1);
      
      // Add new state
      historyRef.current.push(newDocument);
      historyIndexRef.current = historyRef.current.length - 1;
      
      // Limit history size
      if (historyRef.current.length > MAX_HISTORY_SIZE) {
        historyRef.current = historyRef.current.slice(-MAX_HISTORY_SIZE);
        historyIndexRef.current = historyRef.current.length - 1;
      }
    }
    
    lastChangeTimeRef.current = now;
    forceUpdate({});
  }, []);

  // Wrapper for setDocument that also pushes to history
  const setDocument = useCallback((
    updater: EmailDocument | ((prev: EmailDocument) => EmailDocument),
    immediate = false
  ) => {
    setDocumentInternal(prev => {
      const newDoc = typeof updater === 'function' ? updater(prev) : updater;
      pushToHistory(newDoc, immediate);
      return newDoc;
    });
  }, [pushToHistory]);

  // Undo action
  const undo = useCallback(() => {
    if (historyIndexRef.current > 0) {
      historyIndexRef.current -= 1;
      const prevDocument = historyRef.current[historyIndexRef.current];
      setDocumentInternal(prevDocument);
      setSelection({ type: null });
      forceUpdate({});
    }
  }, []);

  // Redo action
  const redo = useCallback(() => {
    if (historyIndexRef.current < historyRef.current.length - 1) {
      historyIndexRef.current += 1;
      const nextDocument = historyRef.current[historyIndexRef.current];
      setDocumentInternal(nextDocument);
      setSelection({ type: null });
      forceUpdate({});
    }
  }, []);

  // ==========================================================================
  // Row Actions
  // ==========================================================================

  const addRow = useCallback((layout: RowLayout, index?: number) => {
    const newRow = createRow(layout);
    setDocument(prev => {
      const rows = [...prev.rows];
      if (index !== undefined) {
        rows.splice(index, 0, newRow);
      } else {
        rows.push(newRow);
      }
      return { ...prev, rows };
    }, true); // immediate = true for structural changes
    // Select the new row
    setSelection({ type: 'row', rowId: newRow.id });
  }, [setDocument]);

  const deleteRow = useCallback((rowId: string) => {
    setDocument(prev => ({
      ...prev,
      rows: prev.rows.filter(r => r.id !== rowId),
    }), true);
    setSelection({ type: null });
  }, [setDocument]);

  const moveRow = useCallback((fromIndex: number, toIndex: number) => {
    setDocument(prev => {
      const rows = [...prev.rows];
      const [removed] = rows.splice(fromIndex, 1);
      rows.splice(toIndex, 0, removed);
      return { ...prev, rows };
    }, true);
  }, [setDocument]);

  const updateRow = useCallback((rowId: string, updates: Partial<EmailRow>) => {
    setDocument(prev => ({
      ...prev,
      rows: prev.rows.map(row =>
        row.id === rowId ? { ...row, ...updates } : row
      ),
    }));
  }, [setDocument]);

  const duplicateRow = useCallback((rowId: string) => {
    setDocument(prev => {
      const rowIndex = prev.rows.findIndex(r => r.id === rowId);
      if (rowIndex === -1) return prev;

      const originalRow = prev.rows[rowIndex];
      const duplicatedRow: EmailRow = {
        ...originalRow,
        id: generateId(),
        columns: originalRow.columns.map(col => ({
          ...col,
          id: generateId(),
          blocks: col.blocks.map(block => ({
            ...block,
            id: generateId(),
          })),
        })),
      };

      const rows = [...prev.rows];
      rows.splice(rowIndex + 1, 0, duplicatedRow);
      return { ...prev, rows };
    }, true);
  }, [setDocument]);

  // ==========================================================================
  // Block Actions
  // ==========================================================================

  const findBlockLocation = useCallback((blockId: string) => {
    for (const row of document.rows) {
      for (const col of row.columns) {
        const blockIndex = col.blocks.findIndex(b => b.id === blockId);
        if (blockIndex !== -1) {
          return { rowId: row.id, columnId: col.id, blockIndex };
        }
      }
    }
    return null;
  }, [document.rows]);

  const addBlock = useCallback((columnId: string, type: BlockType, index?: number) => {
    const newBlock = createBlock(type);
    setDocument(prev => ({
      ...prev,
      rows: prev.rows.map(row => ({
        ...row,
        columns: row.columns.map(col => {
          if (col.id !== columnId) return col;
          const blocks = [...col.blocks];
          if (index !== undefined) {
            blocks.splice(index, 0, newBlock);
          } else {
            blocks.push(newBlock);
          }
          return { ...col, blocks };
        }),
      })),
    }), true);
    setSelection({ type: 'block', blockId: newBlock.id });
  }, [setDocument]);

  const deleteBlock = useCallback((blockId: string) => {
    setDocument(prev => ({
      ...prev,
      rows: prev.rows.map(row => ({
        ...row,
        columns: row.columns.map(col => ({
          ...col,
          blocks: col.blocks.filter(b => b.id !== blockId),
        })),
      })),
    }), true);
    setSelection({ type: null });
  }, [setDocument]);

  const moveBlock = useCallback((blockId: string, targetColumnId: string, targetIndex: number) => {
    setDocument(prev => {
      // Find and remove the block from its current location
      let movedBlock: ContentBlock | null = null;
      const rowsWithBlockRemoved = prev.rows.map(row => ({
        ...row,
        columns: row.columns.map(col => {
          const blockIndex = col.blocks.findIndex(b => b.id === blockId);
          if (blockIndex !== -1) {
            movedBlock = col.blocks[blockIndex];
            return {
              ...col,
              blocks: col.blocks.filter(b => b.id !== blockId),
            };
          }
          return col;
        }),
      }));

      if (!movedBlock) return prev;

      // Add the block to the target location
      return {
        ...prev,
        rows: rowsWithBlockRemoved.map(row => ({
          ...row,
          columns: row.columns.map(col => {
            if (col.id !== targetColumnId) return col;
            const blocks = [...col.blocks];
            blocks.splice(targetIndex, 0, movedBlock!);
            return { ...col, blocks };
          }),
        })),
      };
    }, true);
  }, [setDocument]);

  const updateBlock = useCallback((blockId: string, updates: Partial<ContentBlock>) => {
    setDocument(prev => ({
      ...prev,
      rows: prev.rows.map(row => ({
        ...row,
        columns: row.columns.map(col => ({
          ...col,
          blocks: col.blocks.map(block =>
            block.id === blockId ? { ...block, ...updates } as ContentBlock : block
          ),
        })),
      })),
    })); // debounced for property changes like color/size
  }, [setDocument]);

  const duplicateBlock = useCallback((blockId: string) => {
    const location = findBlockLocation(blockId);
    if (!location) return;

    setDocument(prev => ({
      ...prev,
      rows: prev.rows.map(row => ({
        ...row,
        columns: row.columns.map(col => {
          if (col.id !== location.columnId) return col;
          const blocks = [...col.blocks];
          const originalBlock = blocks[location.blockIndex];
          const duplicatedBlock = { ...originalBlock, id: generateId() };
          blocks.splice(location.blockIndex + 1, 0, duplicatedBlock);
          return { ...col, blocks };
        }),
      })),
    }), true);
  }, [findBlockLocation, setDocument]);

  // ==========================================================================
  // Selection Actions
  // ==========================================================================

  const select = useCallback((newSelection: Selection) => {
    setSelection(newSelection);
  }, []);

  const clearSelection = useCallback(() => {
    setSelection({ type: null });
  }, []);

  // ==========================================================================
  // Global Actions
  // ==========================================================================

  const updateGlobalStyles = useCallback((updates: Partial<GlobalStyles>) => {
    setDocument(prev => ({
      ...prev,
      globalStyles: { ...prev.globalStyles, ...updates },
    }));
  }, [setDocument]);

  const reset = useCallback(() => {
    setDocument(initialDocument, true);
    setSelection({ type: null });
  }, [setDocument]);

  const loadDocument = useCallback((doc: EmailDocument) => {
    // When loading a document, reset history to start fresh
    historyRef.current = [doc];
    historyIndexRef.current = 0;
    setDocumentInternal(doc);
    setSelection({ type: null });
    forceUpdate({});
  }, []);

  return {
    document,
    selection,
    canUndo,
    canRedo,
    addRow,
    deleteRow,
    moveRow,
    updateRow,
    duplicateRow,
    addBlock,
    deleteBlock,
    moveBlock,
    updateBlock,
    duplicateBlock,
    select,
    clearSelection,
    updateGlobalStyles,
    reset,
    loadDocument,
    undo,
    redo,
  };
}
